import { useEffect, useState } from "react";
import { Copy, KeyRound, Link2, Loader2, Trash2, PauseCircle, PlayCircle, RefreshCw, ShieldAlert, Users, X } from "lucide-react";
import axios from "axios";

import { adminSetPassword, dismissPasswordRequest, fetchPasswordRequests } from "../lib/api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

async function fetchUsers() {
  const res = await axios.get(`${API}/admin/users`);
  return res.data;
}
async function deleteUser(email) {
  await axios.delete(`${API}/admin/users/${encodeURIComponent(email)}`);
}
async function toggleSuspend(email, suspended) {
  await axios.patch(`${API}/admin/users/${encodeURIComponent(email)}/suspend?suspended=${suspended}`);
}
async function createResetLink(email) {
  const res = await axios.post(`${API}/admin/users/${encodeURIComponent(email)}/reset-link`);
  return res.data;
}
async function changeRole(email, role) {
  await axios.patch(`${API}/admin/users/${encodeURIComponent(email)}/role`, { role });
}

const ROLE_BADGE = {
  admin:      "bg-purple-100 text-purple-700",
  user:       "bg-blue-100 text-blue-700",
  team_admin: "bg-teal-100 text-teal-700",
  client:     "bg-slate-100 text-slate-600",
};

const ROLE_LABEL = {
  admin:      "Admin",
  user:       "User",
  team_admin: "Team Admin",
  client:     "Client",
};

const SUB_BADGE = {
  active:   "bg-emerald-100 text-emerald-700",
  pending:  "bg-yellow-100 text-yellow-700",
  past_due: "bg-orange-100 text-orange-700",
  canceled: "bg-red-100 text-red-600",
};

function fmt(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

function fmtTime(iso) {
  if (!iso) return "Never";
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

export default function AdminUsersPanel({ onPasswordRequestsChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(null); // email being actioned
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all"); // "all" | "user" | "admin" | "client"

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const d = await fetchUsers();
      setData(d);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load users.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (email) => {
    if (!window.confirm(`Permanently delete ${email}? This cannot be undone.`)) return;
    setWorking(email);
    try {
      await deleteUser(email);
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || "Delete failed.");
    } finally {
      setWorking(null);
    }
  };

  const handleRoleChange = async (email, newRole) => {
    setWorking(email);
    try {
      await changeRole(email, newRole);
      await load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => d.msg || JSON.stringify(d)).join("; ")
        : detail || `Role change failed (HTTP ${e.response?.status ?? "network error"}).`;
      setError(msg);
    } finally {
      setWorking(null);
    }
  };

  const handleSuspend = async (email, currentlySuspended) => {
    setWorking(email);
    try {
      await toggleSuspend(email, !currentlySuspended);
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || "Action failed.");
    } finally {
      setWorking(null);
    }
  };

  // A password reset link for a user, emailed when email is set up and
  // shown here to copy either way.
  const [resetLink, setResetLink] = useState(null);
  const handleResetLink = async (email) => {
    setWorking(email);
    setError("");
    try {
      const result = await createResetLink(email);
      setResetLink({ email, ...result, copied: false });
    } catch (e) {
      setError(e.response?.data?.detail || "Could not create a reset link.");
    } finally {
      setWorking(null);
    }
  };

  // Set a password for a user directly: a temporary one by default, which
  // they are asked to replace the next time they sign in.
  const [setting, setSetting] = useState(null);
  const generatePassword = () => {
    const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789";
    const bytes = new Uint32Array(14);
    window.crypto.getRandomValues(bytes);
    return Array.from(bytes, (n) => alphabet[n % alphabet.length]).join("");
  };
  const openSetPassword = (email) =>
    setSetting({
      email, password: generatePassword(), requireChange: true, signOut: true,
      saving: false, done: null, error: "",
    });
  const submitSetPassword = async () => {
    if (!setting) return;
    if ((setting.password || "").length < 8) {
      setSetting((s) => ({ ...s, error: "At least 8 characters." }));
      return;
    }
    setSetting((s) => ({ ...s, saving: true, error: "" }));
    try {
      const result = await adminSetPassword(
        setting.email, setting.password, setting.requireChange, setting.signOut,
      );
      setSetting((s) => ({ ...s, saving: false, done: result.message }));
    } catch (e) {
      setSetting((s) => ({ ...s, saving: false, error: e.response?.data?.detail || "Could not set the password." }));
    }
  };

  // People who clicked "Forgotten your password?" and are waiting for help.
  const [passwordRequests, setPasswordRequests] = useState([]);
  const loadPasswordRequests = async () => {
    try {
      const result = await fetchPasswordRequests();
      setPasswordRequests(result.requests || []);
    } catch {
      setPasswordRequests([]);
    }
    if (onPasswordRequestsChange) onPasswordRequestsChange();
  };
  useEffect(() => {
    loadPasswordRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Setting a password or sending a link answers the request, so the list is
  // re-read after either.
  useEffect(() => {
    if (setting?.done || resetLink) loadPasswordRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setting?.done, resetLink]);

  const users = (data?.users || []).filter(u => filter === "all" || u.role === filter);

  return (
    <div className="space-y-6">
      {passwordRequests.length > 0 && (
        <div className="rounded-xl border border-[#cfa77b] bg-[#fbf6f0] p-4" data-testid="password-requests">
          <p className="text-sm font-semibold text-[#323c44]">
            {passwordRequests.length === 1
              ? "1 person needs help with their password"
              : `${passwordRequests.length} people need help with their password`}
          </p>
          <p className="mt-0.5 text-xs text-[#4a5b68]">
            They clicked "Forgotten your password?" on the sign-in page. Setting a password or sending a
            reset link clears the request.
          </p>
          <ul className="mt-3 divide-y divide-[#eadfd2]">
            {passwordRequests.map((r) => (
              <li key={r.email} className="flex flex-wrap items-center gap-2 py-2">
                <span className="min-w-0 flex-1 truncate text-sm font-medium text-[#323c44]">{r.email}</span>
                <span className="text-xs text-[#7d8b96]">
                  {fmtTime(r.last_requested_at)}
                  {r.times > 1 ? ` · asked ${r.times} times` : ""}
                </span>
                <button
                  type="button"
                  onClick={() => openSetPassword(r.email)}
                  className="rounded-full bg-[#50e3c2] px-3 py-1 text-xs font-bold uppercase tracking-[0.1em] text-[#323c44] hover:bg-[#57c1a9]"
                >
                  Set password
                </button>
                <button
                  type="button"
                  onClick={() => handleResetLink(r.email)}
                  className="rounded-full border border-[#cccccc] bg-white px-3 py-1 text-xs font-semibold text-[#323c44] hover:border-[#57c1a9] hover:text-[#119996]"
                >
                  Reset link
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    await dismissPasswordRequest(r.email).catch(() => {});
                    loadPasswordRequests();
                  }}
                  className="rounded-full px-2 py-1 text-xs font-semibold text-[#7d8b96] hover:text-[#323c44]"
                >
                  Dismiss
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {setting && (
        <div className="rounded-xl border border-[#57c1a9] bg-white p-4 shadow-card" data-testid="set-password-panel">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-[#323c44]">Set a password for {setting.email}</p>
              <p className="mt-0.5 text-xs text-[#4a5b68]">
                A random one is filled in. Send it to them securely, separately from their email address.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setSetting(null)}
              className="rounded p-1 text-[#7d8b96] hover:text-[#323c44]"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          {setting.done ? (
            <div className="mt-3 space-y-2">
              <p className="rounded-lg bg-[#e8faf5] px-3 py-2 text-sm text-[#1f5d74]">{setting.done}</p>
              <div className="flex items-center gap-2">
                <input
                  readOnly
                  value={setting.password}
                  onFocus={(e) => e.target.select()}
                  className="min-w-0 flex-1 rounded-full border border-[#cccccc] bg-[#f5f5f5] px-3 py-1.5 font-mono text-sm text-[#323c44]"
                />
                <button
                  type="button"
                  onClick={() => navigator.clipboard?.writeText(setting.password).catch(() => {})}
                  className="inline-flex items-center gap-1.5 rounded-full border border-[#cccccc] bg-white px-3 py-1.5 text-xs font-semibold text-[#323c44] hover:border-[#57c1a9] hover:text-[#119996]"
                >
                  <Copy className="h-3.5 w-3.5" /> Copy
                </button>
              </div>
            </div>
          ) : (
            <div className="mt-3 space-y-3">
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={setting.password}
                  onChange={(e) => setSetting((s) => ({ ...s, password: e.target.value }))}
                  className="min-w-0 flex-1 rounded-full border border-[#cccccc] px-3 py-1.5 font-mono text-sm text-[#323c44] focus:border-[#57c1a9] focus:outline-none"
                  aria-label="New password"
                  data-testid="admin-new-password"
                />
                <button
                  type="button"
                  onClick={() => setSetting((s) => ({ ...s, password: generatePassword() }))}
                  className="rounded-full border border-[#cccccc] bg-white px-3 py-1.5 text-xs font-semibold text-[#323c44] hover:border-[#57c1a9] hover:text-[#119996]"
                >
                  Generate another
                </button>
              </div>
              <label className="flex items-center gap-2 text-sm text-[#323c44]">
                <input
                  type="checkbox"
                  checked={setting.requireChange}
                  onChange={(e) => setSetting((s) => ({ ...s, requireChange: e.target.checked }))}
                  className="accent-[#57c1a9]"
                />
                Ask them to choose their own when they next sign in
              </label>
              <label className="flex items-center gap-2 text-sm text-[#323c44]">
                <input
                  type="checkbox"
                  checked={setting.signOut}
                  onChange={(e) => setSetting((s) => ({ ...s, signOut: e.target.checked }))}
                  className="accent-[#57c1a9]"
                />
                Sign them out on every device
              </label>
              {setting.error && <p className="text-sm text-red-600">{setting.error}</p>}
              <button
                type="button"
                onClick={submitSetPassword}
                disabled={setting.saving}
                className="inline-flex items-center gap-2 rounded-full bg-[#50e3c2] px-4 py-1.5 text-xs font-bold uppercase tracking-[0.12em] text-[#323c44] hover:bg-[#57c1a9] disabled:opacity-50"
                data-testid="admin-set-password-submit"
              >
                {setting.saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                Set password
              </button>
            </div>
          )}
        </div>
      )}

      {resetLink && (
        <div className="rounded-xl border border-[#57c1a9] bg-[#e8faf5] p-4" data-testid="reset-link-panel">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-[#323c44]">
                Password reset link for {resetLink.email}
              </p>
              <p className="mt-0.5 text-xs text-[#4a5b68]">
                {resetLink.emailed ? "Emailed to them. " : "Email is not set up, so send them this link yourself. "}
                It works once, for {resetLink.expires_in_minutes} minutes.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setResetLink(null)}
              className="rounded p-1 text-[#7d8b96] hover:text-[#323c44]"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="mt-3 flex items-center gap-2">
            <input
              readOnly
              value={resetLink.reset_url}
              onFocus={(e) => e.target.select()}
              className="min-w-0 flex-1 rounded-full border border-[#cccccc] bg-white px-3 py-1.5 font-mono text-xs text-[#323c44]"
            />
            <button
              type="button"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(resetLink.reset_url);
                  setResetLink((current) => current && { ...current, copied: true });
                } catch {
                  /* the field is selectable as a fallback */
                }
              }}
              className="inline-flex items-center gap-1.5 rounded-full border border-[#cccccc] bg-white px-3 py-1.5 text-xs font-semibold text-[#323c44] hover:border-[#57c1a9] hover:text-[#119996]"
            >
              <Copy className="h-3.5 w-3.5" /> {resetLink.copied ? "Copied" : "Copy"}
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-purple-100">
            <ShieldAlert className="h-5 w-5 text-purple-600" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-[#323c44]">Platform Users</h2>
            <p className="text-sm text-[#4a5b68]">
              {data ? `${data.total} total accounts` : "Loading…"}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="flex items-center gap-2 rounded-lg border border-[#dddddd] bg-white px-3 py-2 text-sm text-[#4a5b68] hover:bg-[#efefef] disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      {error && (
        <p className="rounded-lg bg-red-50 px-4 py-2 text-sm text-red-600">{error}</p>
      )}

      {/* Role filter tabs */}
      <div className="flex gap-1 rounded-lg border border-[#dddddd] bg-[#f5f5f5] p-1 w-fit">
        {[["all", "All"], ["user", "User"], ["admin", "Admin"], ["team_admin", "Team Admin"], ["client", "Client"]].map(([r, label]) => (
          <button
            key={r}
            type="button"
            onClick={() => setFilter(r)}
            className={`rounded-md px-3 py-1.5 text-sm font-medium transition ${
              filter === r
                ? "bg-white text-[#323c44] shadow-sm"
                : "text-[#4a5b68] hover:text-[#323c44]"
            }`}
          >
            {label}
            {r !== "all" && data && (
              <span className="ml-1.5 text-xs text-[#7d8b96]">
                ({data.users.filter(u => u.role === r).length})
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-xl border border-[#dddddd] bg-white shadow-sm">
        {loading ? (
          <div className="px-6 py-16 text-center text-sm text-[#4a5b68]">Loading users…</div>
        ) : users.length === 0 ? (
          <div className="px-6 py-16 text-center">
            <Users className="mx-auto mb-3 h-8 w-8 text-[#cccccc]" />
            <p className="text-sm text-[#4a5b68]">No users found.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#dddddd] bg-[#f5f5f5]">
                  <th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.16em] text-[#4a5b68]">Email</th>
                  <th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.16em] text-[#4a5b68]">Role</th>
                  <th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.16em] text-[#4a5b68]">Subscription</th>
                  <th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.16em] text-[#4a5b68]">Portals</th>
                  <th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.16em] text-[#4a5b68]">Signed up</th>
                  <th className="px-5 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.16em] text-[#4a5b68]">Last login</th>
                  <th className="px-5 py-3 text-right text-[11px] font-semibold uppercase tracking-[0.16em] text-[#4a5b68]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u, i) => (
                  <tr
                    key={u.email}
                    className={`border-b border-[#efefef] ${u.suspended ? "bg-red-50/40" : i % 2 === 0 ? "bg-white" : "bg-[#f5f5f5]"}`}
                  >
                    <td className="px-5 py-3 font-medium text-[#323c44]">
                      <div className="flex items-center gap-2">
                        {u.suspended && (
                          <span className="rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-red-600">Suspended</span>
                        )}
                        {u.email}
                      </div>
                      {u.created_via === "public_signup" && (
                        <p className="text-[10px] text-[#7d8b96]">Self-registered</p>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      {u.role === "admin" ? (
                        <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${ROLE_BADGE.admin}`}>
                          Admin
                        </span>
                      ) : (
                        <select
                          value={u.role}
                          disabled={working === u.email}
                          onChange={(e) => handleRoleChange(u.email, e.target.value)}
                          className={`rounded-full px-2.5 py-0.5 text-xs font-semibold border-0 cursor-pointer appearance-none ${ROLE_BADGE[u.role] || "bg-slate-100 text-slate-600"} disabled:opacity-50`}
                        >
                          <option value="client">Client</option>
                          <option value="team_admin">Team Admin</option>
                          <option value="user">User</option>
                        </select>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      {u.subscription_status ? (
                        <div>
                          <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize ${SUB_BADGE[u.subscription_status] || "bg-slate-100 text-slate-600"}`}>
                            {u.subscription_status}
                          </span>
                          {u.subscription_plan && (
                            <p className="mt-0.5 text-[10px] capitalize text-[#7d8b96]">{u.subscription_plan} plan</p>
                          )}
                        </div>
                      ) : (
                        <span className="text-[#7d8b96]">—</span>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      {u.portal_names?.length > 0 ? (
                        <div className="space-y-0.5">
                          {u.portal_names.map((name, i) => (
                            <p key={i} className="text-xs font-medium text-[#323c44] truncate max-w-[160px]" title={name}>{name}</p>
                          ))}
                        </div>
                      ) : (
                        <span className="text-[#7d8b96]">None</span>
                      )}
                    </td>
                    <td className="px-5 py-3 text-[#4a5b68]">{fmt(u.created_at)}</td>
                    <td className="px-5 py-3 text-[#4a5b68]">{fmtTime(u.last_login_at)}</td>
                    <td className="px-5 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          type="button"
                          title="Set password"
                          onClick={() => openSetPassword(u.email)}
                          disabled={working === u.email}
                          className="rounded-lg p-1.5 text-[#7d8b96] transition hover:bg-[#efefef] hover:text-[#119996] disabled:opacity-40"
                          data-testid={`set-password-${u.email}`}
                        >
                          <KeyRound className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          title="Password reset link"
                          onClick={() => handleResetLink(u.email)}
                          disabled={working === u.email || u.suspended}
                          className="rounded-lg p-1.5 text-[#7d8b96] transition hover:bg-[#efefef] hover:text-[#119996] disabled:opacity-40"
                          data-testid={`reset-link-${u.email}`}
                        >
                          <Link2 className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          title={u.suspended ? "Unsuspend" : "Suspend"}
                          onClick={() => handleSuspend(u.email, u.suspended)}
                          disabled={working === u.email}
                          className="rounded-lg p-1.5 text-[#7d8b96] transition hover:bg-[#efefef] hover:text-[#119996] disabled:opacity-40"
                        >
                          {u.suspended
                            ? <PlayCircle className="h-4 w-4" />
                            : <PauseCircle className="h-4 w-4" />
                          }
                        </button>
                        <button
                          type="button"
                          title="Delete user"
                          onClick={() => handleDelete(u.email)}
                          disabled={working === u.email}
                          className="rounded-lg p-1.5 text-[#7d8b96] transition hover:bg-red-50 hover:text-red-500 disabled:opacity-40"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
